import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type Konva from "konva";
import type { BuilderItem } from "../types/gangsheet";
import GangsheetCanvas from "../components/gangsheet/GangsheetCanvas";
import GangsheetControls from "../components/gangsheet/GangsheetControls";
import GangsheetSelectionPanel from "../components/gangsheet/GangsheetSelectionPanel";
import GangsheetArtworkList from "../components/gangsheet/GangsheetArtworkList";
import GangsheetStats from "../components/gangsheet/GangsheetStats";
import {
  pageWrapStyle,
  panelStyle,
  shellStyle,
  ActionButton,
  MiniStatCard,
  Pill,
  primaryButtonStyle,
  secondaryButtonStyle,
  dangerButtonStyle,
} from "../components/gangsheet/GangsheetUI";
import {
  SHEET_WIDTH_IN,
  DEFAULT_SPACING_IN,
  MAX_UPLOADS,
  autoNestItems,
  clampCenterToSheet,
  getAxisAlignedBounds,
  getDpi,
  getRotatedCorners,
  inchesToPreviewPx,
  polygonsIntersect,
  rotateSelectedByQuarterTurn,
  uuid,
} from "../utils/gangsheetMath";
import {
  buildDefaultSizeInches,
  loadImage,
  trimTransparentEdges,
} from "../utils/gangsheetImage";
import { exportGangsheetPng } from "../utils/gangsheetExport";

export default function GangsheetBuilderPage() {
  const [sheetLengthIn, setSheetLengthIn] = useState<number>(24);
  const [spacingIn, setSpacingIn] = useState<number>(DEFAULT_SPACING_IN);
  const [items, setItems] = useState<BuilderItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);
  const [stageWidth, setStageWidth] = useState<number>(inchesToPreviewPx(SHEET_WIDTH_IN));
  const [allowAutoRotate, setAllowAutoRotate] = useState(true);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  const isMobile = viewportWidth < 900;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const trRef = useRef<Konva.Transformer | null>(null);
  const shapeRefs = useRef<Record<string, Konva.Image | null>>({});

  useLayoutEffect(() => {
    window.scrollTo(0, 0);

    const raf = window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      if (containerRef.current) {
        containerRef.current.scrollIntoView({
          block: "start",
          behavior: "auto",
        });
      }
    });

    return () => window.cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const handleViewportResize = () => {
      setViewportWidth(window.innerWidth);
    };

    handleViewportResize();
    window.addEventListener("resize", handleViewportResize);
    return () => window.removeEventListener("resize", handleViewportResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const maxCanvasWidth = inchesToPreviewPx(SHEET_WIDTH_IN);

      const targetWidth = isMobile
        ? Math.min(Math.max(containerWidth - 12, 300), maxCanvasWidth)
        : Math.min(Math.max(containerWidth - 20, 320), maxCanvasWidth);

      setStageWidth(targetWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const stageScale = stageWidth / inchesToPreviewPx(SHEET_WIDTH_IN);
  const stageHeight = inchesToPreviewPx(sheetLengthIn) * stageScale;

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId]
  );

  const overlapMap = useMemo(() => {
    const overlaps = new Set<string>();

    for (let i = 0; i < items.length; i += 1) {
      for (let j = i + 1; j < items.length; j += 1) {
        const a = getRotatedCorners(items[i]);
        const b = getRotatedCorners(items[j]);
        if (polygonsIntersect(a, b)) {
          overlaps.add(items[i].id);
          overlaps.add(items[j].id);
        }
      }
    }

    return overlaps;
  }, [items]);

  const outOfBoundsMap = useMemo(() => {
    const bad = new Set<string>();

    items.forEach((item) => {
      const bounds = getAxisAlignedBounds(item);
      const exceeds =
        bounds.x < 0 ||
        bounds.y < 0 ||
        bounds.x + bounds.width > SHEET_WIDTH_IN ||
        bounds.y + bounds.height > sheetLengthIn;

      if (exceeds) {
        bad.add(item.id);
      }
    });

    return bad;
  }, [items, sheetLengthIn]);

  const lowDpiMap = useMemo(() => {
    const bad = new Set<string>();

    items.forEach((item) => {
      if (getDpi(item) < 300) {
        bad.add(item.id);
      }
    });
    return bad;
  }, [items]);

  const usedLengthIn = useMemo(() => {
    if (!items.length) return 0;
    return Math.min(
      sheetLengthIn,
      Math.max(
        ...items.map((item) => {
          const bounds = getAxisAlignedBounds(item);
          return bounds.y + bounds.height;
        })
      )
    );
  }, [items, sheetLengthIn]);

  const remainingLengthIn = Math.max(0, sheetLengthIn - usedLengthIn);
  const hasOverlap = overlapMap.size > 0;
  const hasBoundsIssue = outOfBoundsMap.size > 0;
  const hasLowDpi = lowDpiMap.size > 0;
  const canExport = items.length > 0 && !hasOverlap && !hasBoundsIssue;

  useEffect(() => {
    if (!trRef.current || !selectedId) {
      trRef.current?.nodes([]);
      trRef.current?.getLayer()?.batchDraw();
      return;
    }

    const node = shapeRefs.current[selectedId];
    if (node) {
      trRef.current.nodes([node]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [selectedId, items]);

  const updateItem = useCallback(
    (id: string, updater: (item: BuilderItem) => BuilderItem) => {
      setItems((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
    },
    []
  );

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;

      const availableSlots = Math.max(0, MAX_UPLOADS - items.length);
      const fileArray = Array.from(files).slice(0, availableSlots);

      if (!fileArray.length) {
        setMessage(`Maximum ${MAX_UPLOADS} images allowed.`);
        return;
      }

      setBusy(true);
      setMessage("");

      try {
        const newItems: BuilderItem[] = [];
        const existing = [...items];

        for (const file of fileArray) {
          const trimmed = await trimTransparentEdges(file);
          const imageEl = await loadImage(trimmed.dataUrl);
          const { widthIn, heightIn } = buildDefaultSizeInches(trimmed.width, trimmed.height);

          const nextY =
            existing.length + newItems.length === 0
              ? spacingIn + heightIn / 2
              : Math.min(
                  sheetLengthIn - spacingIn - heightIn / 2,
                  Math.max(
                    spacingIn + heightIn / 2,
                    [...existing, ...newItems].reduce((max, item) => {
                      const bounds = getAxisAlignedBounds(item);
                      return Math.max(max, bounds.y + bounds.height + spacingIn + heightIn / 2);
                    }, spacingIn + heightIn / 2)
                  )
                );

          newItems.push(
            clampCenterToSheet(
              {
                id: uuid(),
                name: file.name,
                src: trimmed.dataUrl,
                imageEl,
                naturalWidth: trimmed.width,
                naturalHeight: trimmed.height,
                centerX: spacingIn + widthIn / 2,
                centerY: nextY,
                widthIn,
                heightIn,
                rotation: 0,
              },
              sheetLengthIn
            )
          );
        }

        setItems((prev) => [...prev, ...newItems]);

        if (newItems[0]) {
          setSelectedId(newItems[0].id);
        }

        if (items.length + fileArray.length >= MAX_UPLOADS) {
          setMessage(`Loaded artwork. Maximum ${MAX_UPLOADS} images reached.`);
        }
      } catch (error) {
        console.error(error);
        setMessage("Something went wrong while loading one of the images.");
      } finally {
        setBusy(false);
      }
    },
    [items, sheetLengthIn, spacingIn]
  );

  const removeSelected = useCallback(() => {
    if (!selectedId) return;
    setItems((prev) => prev.filter((item) => item.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);

  const duplicateSelected = useCallback(() => {
    if (!selectedItem) return;

    const duplicate = clampCenterToSheet(
      {
        ...selectedItem,
        id: uuid(),
        centerX: selectedItem.centerX + 0.5,
        centerY: selectedItem.centerY + 0.5,
      },
      sheetLengthIn
    );

    setItems((prev) => [...prev, duplicate]);
    setSelectedId(duplicate.id);
  }, [selectedItem, sheetLengthIn]);

  const rotateSelected = useCallback(() => {
    if (!selectedItem) return;

    updateItem(selectedItem.id, (item) => rotateSelectedByQuarterTurn(item, sheetLengthIn));
  }, [selectedItem, sheetLengthIn, updateItem]);

  const clearAll = useCallback(() => {
    setItems([]);
    setSelectedId(null);
    setMessage("");
  }, []);

  const autoNest = useCallback(() => {
    if (!items.length) return;

    const result = autoNestItems(items, sheetLengthIn, spacingIn, allowAutoRotate);
    setItems(result.items);
    setMessage(
      result.overflow
        ? "Some artwork pushed past the selected sheet length. Increase the length or reduce the layout."
        : "Auto nest complete."
    );
  }, [allowAutoRotate, items, sheetLengthIn, spacingIn]);

  const exportPng = useCallback(async () => {
    if (!canExport) {
      setMessage("Fix overlaps or out-of-bounds artwork before exporting.");
      return;
    }

    if (sheetLengthIn > 70) {
      setMessage("Large export detected. Best results are usually on desktop for 70+ inch sheets.");
    }

    setIsExporting(true);

    try {
      await exportGangsheetPng(items, sheetLengthIn);
      setMessage("Gangsheet export complete.");
    } catch (error) {
      console.error(error);
      setMessage("Export failed. Try a shorter sheet length or use a desktop browser for large sheets.");
    } finally {
      setIsExporting(false);
    }
  }, [canExport, items, sheetLengthIn]);

  const statusPills = (
    <div
      style={{
        marginTop: 12,
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      {!hasOverlap && !hasBoundsIssue && !hasLowDpi && (
        <div
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: "#dbeafe",
            background: "rgba(34,197,94,0.14)",
            border: "1px solid rgba(34,197,94,0.22)",
          }}
        >
          Ready layout
        </div>
      )}

      {hasOverlap && (
        <div
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: "#fecaca",
            background: "rgba(239,68,68,0.14)",
            border: "1px solid rgba(239,68,68,0.22)",
          }}
        >
          Overlap detected
        </div>
      )}

      {hasBoundsIssue && (
        <div
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: "#fde68a",
            background: "rgba(245,158,11,0.14)",
            border: "1px solid rgba(245,158,11,0.22)",
          }}
        >
          Out of bounds
        </div>
      )}

      {hasLowDpi && (
        <div
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: "#bfdbfe",
            background: "rgba(37,99,235,0.14)",
            border: "1px solid rgba(96,165,250,0.22)",
          }}
        >
          Low DPI artwork
        </div>
      )}

      {!!items.length && (
        <div
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.2,
            color: "#cbd5e1",
            background: "rgba(148,163,184,0.12)",
            border: "1px solid rgba(148,163,184,0.18)",
          }}
        >
          {items.length} piece{items.length === 1 ? "" : "s"} loaded
        </div>
      )}
    </div>
  );

  const mobileDesktopRecommendation = isMobile ? (
    <div
      style={{
        marginTop: 14,
        padding: "12px 14px",
        borderRadius: 16,
        border: "1px solid rgba(96,165,250,0.2)",
        background: "linear-gradient(180deg, rgba(30,41,59,0.62), rgba(15,23,42,0.8))",
        boxShadow: "0 10px 30px rgba(2, 6, 23, 0.28)",
        display: "grid",
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 900,
          letterSpacing: 0.25,
          color: "#dbeafe",
        }}
      >
        Best on desktop
      </div>

      <div
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          color: "#93c5fd",
        }}
      >
        This builder works on mobile, but larger layouts and precise arranging are better on desktop or laptop.
      </div>
    </div>
  ) : null;

  const storeIntegrationNote = (
    <div
      style={{
        marginTop: 14,
        padding: "12px 14px",
        borderRadius: 16,
        border: "1px solid rgba(96,165,250,0.2)",
        background: "linear-gradient(180deg, rgba(30,41,59,0.62), rgba(15,23,42,0.8))",
        boxShadow: "0 10px 30px rgba(2, 6, 23, 0.28)",
        display: "grid",
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 900,
          letterSpacing: 0.25,
          color: "#dbeafe",
        }}
      >
        Store Integration
      </div>

      <div
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          color: "#93c5fd",
        }}
      >
        Need a gangsheet builder for your store?{" "}
        <span style={{ fontWeight: 700, color: "#bfdbfe" }}>
          Build A Gang Sheet (Drip Apps)
        </span>{" "}
        is the recommended option for customer-facing e-commerce integration.
      </div>
    </div>
  );

  const topStats = (
    <div
      style={{
        display: "grid",
        gap: 10,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(120px, 1fr))",
      }}
    >
      <MiniStatCard label="Artwork" value={`${items.length}`} />
      <MiniStatCard label="Used" value={`${usedLengthIn.toFixed(2)}"`} />
      <MiniStatCard label="Remaining" value={`${remainingLengthIn.toFixed(2)}"`} />
      <MiniStatCard label="Sheet" value={`22" × ${sheetLengthIn}"`} />
    </div>
  );

  const topActionBar = (
    <div
      style={{
        display: "grid",
        gap: 10,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(6, minmax(120px, 1fr))",
      }}
    >
      <ActionButton
        style={primaryButtonStyle}
        onClick={autoNest}
        disabled={!items.length || busy}
      >
        Auto Nest
      </ActionButton>

      <ActionButton
        style={secondaryButtonStyle}
        onClick={exportPng}
        disabled={!canExport || isExporting}
      >
        {isExporting ? "Exporting..." : "Export PNG"}
      </ActionButton>

      <ActionButton
        style={secondaryButtonStyle}
        onClick={duplicateSelected}
        disabled={!selectedItem}
      >
        Duplicate
      </ActionButton>

      <ActionButton
        style={secondaryButtonStyle}
        onClick={rotateSelected}
        disabled={!selectedItem}
      >
        Rotate 90°
      </ActionButton>

      <ActionButton
        style={secondaryButtonStyle}
        onClick={removeSelected}
        disabled={!selectedItem}
      >
        Remove
      </ActionButton>

      <ActionButton
        style={dangerButtonStyle}
        onClick={clearAll}
        disabled={!items.length}
      >
        Clear All
      </ActionButton>
    </div>
  );

  const canvasPanel = (
    <div style={{ ...panelStyle, padding: 14, display: "grid", gap: 12, minWidth: 0 }}>
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            color: "#eff6ff",
            letterSpacing: -0.3,
          }}
        >
          Builder Canvas
        </div>
        <div style={{ color: "#93c5fd", marginTop: 4, lineHeight: 1.45 }}>
          Drag, rotate, resize, and arrange artwork directly on the sheet.
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <Pill text={`22" fixed width`} />
        <Pill text={`${sheetLengthIn}" length`} />
        <Pill text={`${spacingIn.toFixed(2)}" spacing`} />
      </div>

      <div
        ref={containerRef}
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          borderRadius: 18,
          border: "1px solid rgba(96,165,250,0.15)",
          background: "linear-gradient(180deg, rgba(2,6,23,0.98), rgba(8,15,30,0.98))",
          padding: 10,
        }}
      >
        <GangsheetCanvas
          items={items}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          updateItem={updateItem}
          overlapMap={overlapMap}
          outOfBoundsMap={outOfBoundsMap}
          sheetLengthIn={sheetLengthIn}
          stageWidth={stageWidth}
          stageHeight={stageHeight}
          stageScale={stageScale}
          stageRef={stageRef}
          trRef={trRef}
          shapeRefs={shapeRefs}
        />
      </div>
    </div>
  );

  return (
    <div style={pageWrapStyle}>
      <div style={shellStyle}>
        <div style={{ ...panelStyle, padding: isMobile ? 14 : 18, display: "grid", gap: 14 }}>
          <div
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.3fr) minmax(0, 1fr)",
              alignItems: "start",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  color: "#bfdbfe",
                  background: "rgba(37,99,235,0.14)",
                  border: "1px solid rgba(96,165,250,0.18)",
                  marginBottom: 12,
                }}
              >
                Premium Builder
              </div>

              <div
                style={{
                  fontSize: isMobile ? 24 : 30,
                  lineHeight: 1.08,
                  fontWeight: 900,
                  letterSpacing: -0.7,
                  color: "#eff6ff",
                }}
              >
                Gangsheet Builder
              </div>

              {statusPills}
              {mobileDesktopRecommendation}
              {storeIntegrationNote}
            </div>

            {topStats}
          </div>

          {topActionBar}
        </div>

        {isMobile ? (
          <>
            {canvasPanel}

            <GangsheetControls
              isMobile={isMobile}
              sheetLengthIn={sheetLengthIn}
              setSheetLengthIn={setSheetLengthIn}
              spacingIn={spacingIn}
              setSpacingIn={setSpacingIn}
              allowAutoRotate={allowAutoRotate}
              setAllowAutoRotate={setAllowAutoRotate}
              busy={busy}
              itemsCount={items.length}
              selectedItemExists={!!selectedItem}
              canExport={canExport}
              isExporting={isExporting}
              message={message}
              handleFiles={handleFiles}
              autoNest={autoNest}
              exportPng={exportPng}
              duplicateSelected={duplicateSelected}
              rotateSelected={rotateSelected}
              removeSelected={removeSelected}
              clearAll={clearAll}
            />

            <GangsheetSelectionPanel
              selectedItem={selectedItem}
              sheetLengthIn={sheetLengthIn}
              updateItem={updateItem}
            />

            <GangsheetStats
              usedLengthIn={usedLengthIn}
              remainingLengthIn={remainingLengthIn}
              itemsCount={items.length}
              sheetLengthIn={sheetLengthIn}
              hasOverlap={hasOverlap}
              hasBoundsIssue={hasBoundsIssue}
              hasLowDpi={hasLowDpi}
            />

            <GangsheetArtworkList
              items={items}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              overlapMap={overlapMap}
              outOfBoundsMap={outOfBoundsMap}
              isMobile={isMobile}
            />
          </>
        ) : (
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: "360px minmax(0, 1fr) 360px",
              alignItems: "start",
            }}
          >
            <div style={{ display: "grid", gap: 16 }}>
              <GangsheetControls
                isMobile={isMobile}
                sheetLengthIn={sheetLengthIn}
                setSheetLengthIn={setSheetLengthIn}
                spacingIn={spacingIn}
                setSpacingIn={setSpacingIn}
                allowAutoRotate={allowAutoRotate}
                setAllowAutoRotate={setAllowAutoRotate}
                busy={busy}
                itemsCount={items.length}
                selectedItemExists={!!selectedItem}
                canExport={canExport}
                isExporting={isExporting}
                message={message}
                handleFiles={handleFiles}
                autoNest={autoNest}
                exportPng={exportPng}
                duplicateSelected={duplicateSelected}
                rotateSelected={rotateSelected}
                removeSelected={removeSelected}
                clearAll={clearAll}
              />

              <GangsheetSelectionPanel
                selectedItem={selectedItem}
                sheetLengthIn={sheetLengthIn}
                updateItem={updateItem}
              />
            </div>

            {canvasPanel}

            <div style={{ display: "grid", gap: 16 }}>
              <GangsheetStats
                usedLengthIn={usedLengthIn}
                remainingLengthIn={remainingLengthIn}
                itemsCount={items.length}
                sheetLengthIn={sheetLengthIn}
                hasOverlap={hasOverlap}
                hasBoundsIssue={hasBoundsIssue}
                hasLowDpi={hasLowDpi}
              />

              <GangsheetArtworkList
                items={items}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                overlapMap={overlapMap}
                outOfBoundsMap={outOfBoundsMap}
                isMobile={isMobile}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}